<template>
  <f7-page
    login-screen
    class="main-activation-code"
  >
    <div class="text-align-center">
      <img
        width="280"
        height="auto"
        :src="LOGO_HISPATEC"
      >
    </div>

    <div>
      <f7-list>
        <f7-list-input
          outline
          item-label
          label="Introduce el código de activación"
          info="Ingresa el código de activación aquí; si no lo conoces, pregunta a tu empresa."
          type="text"
          clear-button
          :placeholder="placeholder"
          @input="code = $event.target.value"
        />
      </f7-list>

      <div class="padding-horizontal">
        <f7-button
          col
          button
          large
          raised
          fill
          :disabled="!code"
          @click="handleActivation"
        >
          {{ titleButton }}
        </f7-button>
      </div>

      <div class="display-flex justify-content-center padding-horizontal padding-top">
        <f7-link @click="emitNoActivation">
          {{ t('appActivation.common.withoutApplicationCode') }}
        </f7-link>
      </div>
    </div>
  </f7-page>
</template>

<script setup>
// @ts-check
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { f7 } from 'framework7-vue';
import { ref } from 'vue';
import { IS_DEVELOPMENT, IS_WEB, LOGO_HISPATEC } from '@/js/common/constants';
import { useI18n } from 'vue-i18n';
import { useSettingRef } from '@erpagroapp/settings';

/** @import { RequestParameters } from 'framework7/types' */

const props = defineProps({
  titleButton: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  preloaderDialogMessage: {
    type: String,
    default: null,
  },
  activationErrorMessageTime: {
    type: Number,
    default: 2000,
  },
  activationErrorMessage: {
    type: String,
    default: null,
  },
  endPointForActivation: {
    type: Function,
    default: null,
  },
  urlApiSocialMediaActivation: {
    type: String,
    default: 'http://socialmedia.hispatec.com/api_pro/obtenerConfiguracionApp/',
  },
  codeAplication: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'on-success',
  'no-activation',
]);

const code = ref('');
const configuracion = ref({
  urlApp: '',
  logoApp: '',
});

const { t } = useI18n();
const urlApp = useSettingRef('App.URLWebService');
const logoApp = useSettingRef('App.Logo');

function emitNoActivation() {
  emit('no-activation', configuracion.value);
}

/**
 * Default end point for activation.
 * @param {string} applicationCode - The activation code.
 * @returns {Promise<any>} A promise with the configuration.
 */
function defaultActivationEndpoint(applicationCode) {
  const auth = {
    username: null,
    token: null,
    propietario: applicationCode,
  };

  const data = {
    propietario: applicationCode,
    aplicacion: props.codeAplication,
  };

  // Para evitar problemas de CORS en el entorno local de
  // desarrollo, usamos el servidor de Webpack como proxy
  const localDevProxy = 'http://localhost:8080/api_pro/obtenerConfiguracionApp/';
  const url = IS_DEVELOPMENT && IS_WEB
    ? localDevProxy
    : props.urlApiSocialMediaActivation;

  const dataToSend = {
    auth: JSON.stringify(auth),
    data: JSON.stringify(data),
  };

  return new Promise(((resolve, reject) => {
    post(url, dataToSend, (xhr, status) => {
      try {
        if (status !== 200) {
          reject(xhr.response);
          return;
        }
        const response = JSON.parse(JSON.parse(xhr.response).configuracionJSON).configuracion;
        resolve(response);
      } catch (e) {
        reject(e);
      }
    });
  }));
}

async function handleActivation() {
  HiDialog.Preloader(true, props.preloaderDialogMessage ?? t('appActivation.info.activating'));

  const endPoint = props.endPointForActivation ?? defaultActivationEndpoint;

  try {
    const result = await endPoint(code.value);

    configuracion.value.urlApp = result.urlApp;
    configuracion.value.logoApp = result.logoApp;
    logoApp.value = configuracion.value.logoApp;
    urlApp.value = configuracion.value.urlApp;

    emit('on-success', result);
  } catch {
    const activationErrorMessage = props.activationErrorMessage ?? t('appActivation.info.activationCodeIsNotValid');
    HiNotification.Toast(activationErrorMessage, props.activationErrorMessageTime);
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * Makes a HTTP POST request.
 * @param {RequestParameters['url']} url
 * @param {RequestParameters['data']} data
 * @param {RequestParameters['complete']} callback
 * @param {RequestParameters['timeout']} timeout
 * @returns {void}
 */
function post(url, data, callback, timeout = 20000) {
  // @ts-expect-error skip missing property error
  f7.request({
    url,
    method: 'POST',
    cache: false,
    data,
    timeout,
    complete: callback,
  });
}
</script>
